import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';

const OPERATORS = [
  { value: '=', label: 'Égal à (=)' },
  { value: '!=', label: 'Différent de (!=)' },
  { value: 'IN', label: 'Dans (IN)' },
  { value: 'NOT IN', label: 'Pas dans (NOT IN)' },
  { value: 'IS EMPTY', label: 'Est vide' },
  { value: 'IS NOT EMPTY', label: 'N\'est pas vide' },
];

/**
 * FilterBuilder Component
 * Dynamic form builder for JQL filters
 */
const FilterBuilder = ({ filters, onChange, serviceDeskConfig, isSuperAdmin = false, allServiceDesks = [], onServiceDeskChange = null, selectedServiceDeskId = null, hideServiceDeskField = false }) => {
  const [localFilters, setLocalFilters] = useState(filters || {
    project: '',
    requestType: '',
    customFields: []
  });

  // Sync local state when filters prop changes (for edit mode)
  useEffect(() => {
    if (filters) {
      setLocalFilters(filters);
    }
  }, [filters]);

  // Get available field keys from service desk config
  const availableFields = serviceDeskConfig?.fieldsMapping
    ? Object.keys(serviceDeskConfig.fieldsMapping).filter((field) => ['sector', 'brand'].includes(field))
    : [];

  // Auto-sync project from serviceDeskConfig.projectKey (only for non-superadmin)
  useEffect(() => {
    if (!isSuperAdmin && serviceDeskConfig?.projectKey && localFilters.project !== serviceDeskConfig.projectKey) {
      const updated = { ...localFilters, project: serviceDeskConfig.projectKey };
      setLocalFilters(updated);
      onChange(updated);
    }
  }, [serviceDeskConfig?.projectKey, isSuperAdmin]);

  const handleProjectChange = (value) => {
    const updated = { ...localFilters, project: value };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleRequestTypeChange = (value) => {
    const updated = { ...localFilters, requestType: value };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleAddCustomField = () => {
    const updated = {
      ...localFilters,
      customFields: [
        ...localFilters.customFields,
        { fieldKey: '', operator: '=', value: '' }
      ]
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleRemoveCustomField = (index) => {
    const updated = {
      ...localFilters,
      customFields: localFilters.customFields.filter((_, i) => i !== index)
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleCustomFieldChange = (index, field, value) => {
    const updated = {
      ...localFilters,
      customFields: localFilters.customFields.map((cf, i) => {
        if (i === index) {
          const newField = { ...cf, [field]: value };

          // Reset value when operator changes to IS EMPTY or IS NOT EMPTY
          if (field === 'operator' && (value === 'IS EMPTY' || value === 'IS NOT EMPTY')) {
            newField.value = null;
          }

          // Convert value to array for IN/NOT IN operators
          if (field === 'operator' && (value === 'IN' || value === 'NOT IN') && typeof newField.value === 'string') {
            newField.value = newField.value ? [newField.value] : [];
          }

          // Convert array to string for other operators
          if (field === 'operator' && value !== 'IN' && value !== 'NOT IN' && Array.isArray(newField.value)) {
            newField.value = newField.value[0] || '';
          }

          return newField;
        }
        return cf;
      })
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleArrayValueChange = (index, arrayIndex, value) => {
    const updated = {
      ...localFilters,
      customFields: localFilters.customFields.map((cf, i) => {
        if (i === index) {
          const newValues = [...(cf.value || [])];
          newValues[arrayIndex] = value;
          return { ...cf, value: newValues };
        }
        return cf;
      })
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleAddArrayValue = (index) => {
    const updated = {
      ...localFilters,
      customFields: localFilters.customFields.map((cf, i) => {
        if (i === index) {
          return { ...cf, value: [...(cf.value || []), ''] };
        }
        return cf;
      })
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleRemoveArrayValue = (index, arrayIndex) => {
    const updated = {
      ...localFilters,
      customFields: localFilters.customFields.map((cf, i) => {
        if (i === index) {
          return { ...cf, value: (cf.value || []).filter((_, ai) => ai !== arrayIndex) };
        }
        return cf;
      })
    };
    setLocalFilters(updated);
    onChange(updated);
  };

  return (
    <div className="space-y-4">
      {/* Service Desk / Project Filter - hidden when managed externally */}
      {!hideServiceDeskField && (
        <div className="space-y-2">
          <Label htmlFor="filter-project">Service Desk / Projet Jira *</Label>
          {isSuperAdmin ? (
            <select
              id="filter-project"
              value={selectedServiceDeskId || ''}
              onChange={(e) => {
                const sdId = e.target.value;
                const selectedSd = allServiceDesks.find(sd => sd.id.toString() === sdId);
                if (selectedSd) {
                  handleProjectChange(selectedSd.projectKey);
                  if (onServiceDeskChange) {
                    onServiceDeskChange(sdId);
                  }
                } else {
                  handleProjectChange('');
                  if (onServiceDeskChange) {
                    onServiceDeskChange('');
                  }
                }
              }}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="">Sélectionner un service desk...</option>
              {allServiceDesks.map((sd) => (
                <option key={sd.id} value={sd.id}>
                  {sd.name} ({sd.projectKey})
                </option>
              ))}
            </select>
          ) : (
            <Input
              id="filter-project"
              value={serviceDeskConfig?.name ? `${serviceDeskConfig.name} (${serviceDeskConfig.projectKey})` : (serviceDeskConfig?.projectKey || localFilters.project || '')}
              disabled
              className="w-full bg-gray-100"
            />
          )}
          <p className="text-xs text-gray-500">
            {isSuperAdmin ? 'Sélectionnez le service desk cible' : 'Défini automatiquement à partir du service desk actuel'}
          </p>
        </div>
      )}

      {/* Request Type Filter - hidden when managed externally */}
      {!hideServiceDeskField && (
        <div className="space-y-2">
          <Label htmlFor="filter-request-type">Type de demande</Label>
          <Input
            id="filter-request-type"
            placeholder="Ex: Demande d'envoi d'élement manquant"
            value={localFilters.requestType || ''}
            onChange={(e) => handleRequestTypeChange(e.target.value)}
            className="w-full"
          />
          <p className="text-xs text-gray-500">Nom exact du type de demande dans Jira (optionnel)</p>
        </div>
      )}

      {/* Custom Fields */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Champs personnalisés</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddCustomField}
            className="h-8"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter un champ
          </Button>
        </div>

        {localFilters.customFields && localFilters.customFields.length > 0 ? (
          <div className="space-y-3">
            {localFilters.customFields.map((field, index) => (
              <div key={index} className="p-4 border rounded-lg bg-gray-50 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    {/* Field Key */}
                    <div className="space-y-1">
                      <Label className="text-xs">Champ</Label>
                      <select
                        value={field.fieldKey || ''}
                        onChange={(e) => handleCustomFieldChange(index, 'fieldKey', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      >
                        <option value="">Sélectionner...</option>
                        {availableFields.map((fieldKey) => (
                          <option key={fieldKey} value={fieldKey}>
                            {fieldKey}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Operator */}
                    <div className="space-y-1">
                      <Label className="text-xs">Opérateur</Label>
                      <select
                        value={field.operator || '='}
                        onChange={(e) => handleCustomFieldChange(index, 'operator', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md text-sm"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomField(index)}
                    className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Supprimer ce filtre"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Value(s) */}
                {field.operator !== 'IS EMPTY' && field.operator !== 'IS NOT EMPTY' && (
                  <div className="space-y-2">
                    <Label className="text-xs">Valeur(s)</Label>
                    {field.operator === 'IN' || field.operator === 'NOT IN' ? (
                      // Multiple values for IN/NOT IN
                      <div className="space-y-2">
                        {(field.value || ['']).map((val, valIndex) => (
                          <div key={valIndex} className="flex items-center gap-2">
                            <Input
                              placeholder="Valeur"
                              value={val}
                              onChange={(e) => handleArrayValueChange(index, valIndex, e.target.value)}
                              className="flex-1"
                            />
                            {(field.value || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveArrayValue(index, valIndex)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddArrayValue(index)}
                          className="h-8"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Ajouter une valeur
                        </Button>
                      </div>
                    ) : (
                      // Single value
                      <Input
                        placeholder="Valeur"
                        value={field.value || ''}
                        onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">Aucun filtre de champ personnalisé. Cliquez sur "Ajouter un champ" pour en créer un.</p>
        )}
      </div>
    </div>
  );
};

FilterBuilder.propTypes = {
  filters: PropTypes.shape({
    project: PropTypes.string,
    requestType: PropTypes.string,
    customFields: PropTypes.arrayOf(
      PropTypes.shape({
        fieldKey: PropTypes.string,
        operator: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
      })
    )
  }),
  onChange: PropTypes.func.isRequired,
  serviceDeskConfig: PropTypes.object,
  isSuperAdmin: PropTypes.bool,
  allServiceDesks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      projectKey: PropTypes.string
    })
  ),
  onServiceDeskChange: PropTypes.func,
  selectedServiceDeskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideServiceDeskField: PropTypes.bool
};

FilterBuilder.defaultProps = {
  filters: {
    project: '',
    requestType: '',
    customFields: []
  },
  serviceDeskConfig: {},
  isSuperAdmin: false,
  allServiceDesks: [],
  onServiceDeskChange: null,
  selectedServiceDeskId: null,
  hideServiceDeskField: false
};

export default FilterBuilder;
